import {observer} from "mobx-react-lite";
import {useEffect} from "react";
import {useNavigate, useOutletContext} from "react-router-dom";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import {ExpandCallapseStore} from "../../../../hooks/expand-collapse-hook";
import {RETURN_METHOD_CATEGORY} from "../../return-method-types";
import returnMethodsListStore from "../../stores/return-methods-list-store";
import ReturnMethodCategoryCard from "./return-method-category-card/return-method-category-card";
import SettingsCard from "../return-method-editor/settings-card/settings-card";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";


const ReturnMethodsList: React.FC = observer(() => {
    const {setSubTitles, setInstruction} = usePage();
    const navigate = useNavigate();

    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();
    const returnMethodsCategories = returnMethodsListStore.returnMethodCategories;

    const shipWithAnyCarrierCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER);
    const returnToDropOffLocationCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION);
    const noShipmentRequiredCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_NO_SHIPMENT_REQUIRED);
    const shipWithPrepaidLabelCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL);

    /** Filter categories based on capability support */
    const visibleCategories = returnMethodsCategories.filter(category => {
        switch (category.category) {
            case RETURN_METHOD_CATEGORY.SHIP_WITH_ANY_CARRIER:
                return shipWithAnyCarrierCapability.isReturnMethodListSupported;
            case RETURN_METHOD_CATEGORY.DROP_OFF_LOCATION:
                return returnToDropOffLocationCapability.isReturnMethodListSupported;
            case RETURN_METHOD_CATEGORY.NO_SHIPMENT_REQUIRED:
                return noShipmentRequiredCapability.isSupported;
            case RETURN_METHOD_CATEGORY.SHIP_WITH_PRE_PAID_LABEL:
                return shipWithPrepaidLabelCapability.isSupported;
            default:
                return true;
        }
    });

    useEffect(() => {
        setSubTitles(['Return Methods']);
    }, [setSubTitles]);

    useEffect(() => {
        setInstruction('Define the methods you will offer to customers for returning their items.');
    }, [setInstruction]);


    const textByCategoryMap = {
        [RETURN_METHOD_CATEGORY.NO_SHIPMENT_REQUIRED]: {
            title: 'No shipment required',
        },
        [RETURN_METHOD_CATEGORY.DROP_OFF_LOCATION]: {
            title: 'Return to drop-off location',
        },
        [RETURN_METHOD_CATEGORY.SHIP_WITH_ANY_CARRIER]: {
            title: 'Ship with any carrier',
        },
        [RETURN_METHOD_CATEGORY.SHIP_WITH_PRE_PAID_LABEL]: {
            title: 'Ship with pre-paid label',
        },
    }

    const onAddReturnMethodClicked = (returnMethodCategory: RETURN_METHOD_CATEGORY) => {
        const returnMethodId = returnMethodsListStore.addReturnMethod(returnMethodCategory);

        if (!returnMethodId) {
            /* this should never happen */
            throw new Error('onAddReturnMethodClicked was not able to generate new return method');
        }

        navigate({
            pathname: returnMethodId
        });
    }

    return (
        <EditorPageLayout
            toolbar={{
                hasBack: false,
                expandAllButton: {
                    expanded: expandCollapseStore.isAllExpanded(),
                    onClick: expandCollapseStore.toggleAllExpanded,
                },
                isLoading: returnMethodsListStore.isLoading
            }}
        >
            <ListLayout>
                <SettingsCard expandCollapseStore={expandCollapseStore}/>
                {visibleCategories.map(returnMethodsCategory =>
                    <ReturnMethodCategoryCard
                        key={returnMethodsCategory.category}
                        title={textByCategoryMap[returnMethodsCategory.category].title}
                        isExpanded={expandCollapseStore.getIsExpanded(returnMethodsCategory.category)}
                        onAddReturnMethodClicked={() => onAddReturnMethodClicked(returnMethodsCategory.category)}
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded(returnMethodsCategory.category, v)}
                        returnMethodCategory={returnMethodsCategory}
                    />
                )}
            </ListLayout>
        </EditorPageLayout>
    )
});

export default ReturnMethodsList;
